'use client';
import React, { useRef, useEffect } from 'react';
import Image from 'next/image';
import Swiper from 'swiper/bundle';

export default function TimelineSlider({ block, position }) {
  const { title, description, slides, _metadata } = block.timeline_slider;
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);

  useEffect(() => {
    const slider = new Swiper(sliderRef.current, {
      slidesPerView: 1,
      centeredSlides: true,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
      autoplay: {
        delay: 3000,
        disableOnInteraction: true
      },
      a11y: {
        notificationClass: 'swiper-notification sr-only'
      }
    });
    const thumbs = new Swiper(thumbRef.current, {
      slidesPerView: 'auto',
      centeredSlides: true,
      slideToClickedSlide: true,
      disableOnInteraction: true
    });

    // Make sliders detect change on each other
    slider.controller.control = thumbs;
    thumbs.controller.control = slider;

    return () => {
      slider.destroy(true, true);
      thumbs.destroy(true, true);
    };
  }, []);

  // Pause autoplay on gallery slider if years are clicked using ref
  function yearClick() {
    if (sliderRef.current?.swiper) {
      sliderRef.current.swiper.autoplay.stop();
    }
  }

  return (
    <section
      id={`${_metadata.uid}_${position}`}
      data-name="timeline-slider"
      className="max-w-container mx-auto overflow-x-hidden py-20 lg:px-16 lg:py-[120px] xl:px-0"
    >
      <div className="mx-auto mb-10 px-5 text-center sm:px-15 lg:mb-15 lg:px-[120px]">
        <h2 className="h4 lg:h2 mb-4">{title}</h2>
        <p className="body-r">{description}</p>
      </div>
      <div className="relative" onClick={yearClick}>
        <GradientOverlays />
        <div
          className="swiper-container max-w-container relative mx-auto overflow-x-hidden sm:pr-10"
          ref={thumbRef}
        >
          <div className="swiper-wrapper">
            {slides.map((slide, index) => (
              <TimelineThumbnail key={index} slide={slide} />
            ))}
          </div>
        </div>
        <div className="border-dawn-grey mx-5 border-b sm:mx-15 lg:mx-0" />
      </div>
      <div
        className="mt-10 lg:mt-15"
        style={{
          '--swiper-navigation-color': '#000',
          '--swiper-navigation-size': '25px'
        }}
      >
        <div className="swiper-container relative" ref={sliderRef}>
          <div className="swiper-wrapper h-full">
            {slides.map((slide, index) => (
              <TimelineSlide key={index} slide={slide} />
            ))}
          </div>
          <div className="swiper-button-prev invisible absolute top-[50%] left-10 lg:visible" />
          <div className="swiper-button-next invisible lg:visible" />
        </div>
      </div>
    </section>
  );
}

const GradientOverlays = () => (
  <>
    <div className="absolute top-0 right-0 z-20 h-[81px] w-5 bg-white sm:w-15 lg:w-0" />
    <div className="absolute top-0 left-0 z-20 h-[81px] w-5 bg-white sm:w-15 lg:w-0" />
    <div className="absolute top-0 left-5 z-20 h-[81px] w-10 bg-linear-to-r from-white to-transparent sm:left-15 lg:left-0" />
    <div className="absolute top-0 right-5 z-20 h-[81px] w-10 bg-linear-to-l from-white to-transparent sm:right-15 lg:right-0" />
  </>
);

const TimelineThumbnail = ({ slide }) => {
  // Has darker text and size when .swiper-slide-active is present
  return (
    <button className="swiper-slide h6 sm:h3 [&.swiper-slide-active]:h3 [&.swiper-slide-active]:sm:feature-number group w-20! self-end sm:w-[140px]!">
      <p className="timeline-year-text text-grey-text group-[.swiper-slide-active]:text-dark-grey-text text-center">
        {slide.year}
      </p>
      <div className="timeline-tick border-dawn-grey group-[.swiper-slide-active]:border-dark-grey-text mt-2 ml-[50%] h-5 border-l group-[.swiper-slide-active]:border-l-4" />
    </button>
  );
};

const TimelineSlide = ({ slide }) => {
  return (
    <div className="swiper-slide relative h-full px-5 sm:px-15 lg:px-[128px]">
      <div className="mb-10 lg:mb-15 lg:max-h-[531px]">
        <Image
          src={slide.image.url}
          width={942}
          height={530}
          alt={slide.image.title.replace(/\.[^/.]+$/, '')}
          sizes="(min-width: 1024px) 942px, (min-width: 640px) 903px, 599px"
          className="lg:max-h-[531px]"
        />
      </div>
      <p className="h6 sm:h5 lg:h4 mx-auto w-full text-center">
        {slide.description}
      </p>
    </div>
  );
};
